import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from './authService';

// Async thunk for Google Sign-In
export const googleSignIn = createAsyncThunk(
  'auth/googleSignIn',
  async ({ idToken, existingToken }, { rejectWithValue }) => {
    try {
      const response = await authService.googleSignIn(idToken, existingToken);

      if (response.success && response.data) {
        // Store token in localStorage
        localStorage.setItem('cleanStudioToken', response.data.access_token);
        localStorage.setItem('cleanStudioUser', JSON.stringify({
          userId: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          isAuthenticated: true,
          authType: 'google',
        }));
        return response.data;
      }

      return rejectWithValue(response.message || 'Sign-in failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Sign-in failed');
    }
  }
);

// Async thunk for creating anonymous token
export const createAnonymousToken = createAsyncThunk(
  'auth/createAnonymousToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.createAnonymousToken();

      if (response.success && response.data) {
        localStorage.setItem('cleanStudioToken', response.data.access_token);
        return response.data;
      }

      return rejectWithValue(response.message || 'Failed to create token');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create token');
    }
  }
);

// Async thunk for email/password signup
export const signup = createAsyncThunk(
  'auth/signup',
  async ({ email, password, name, existingToken }, { rejectWithValue }) => {
    try {
      const response = await authService.signup(email, password, name, existingToken);

      if (response.success && response.data) {
        // Don't store token - email verification required first
        // Clear any existing auth state
        localStorage.removeItem('cleanStudioToken');
        localStorage.removeItem('cleanStudioUser');
        return response.data;
      }

      return rejectWithValue(response.message || 'Signup failed');
    } catch (error) {
      // Pass through error data for handling unverified accounts
      return rejectWithValue({
        message: error.message || 'Signup failed',
        data: error.data || null,
      });
    }
  }
);

// Async thunk for email/password login
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, existingToken }, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password, existingToken);

      if (response.success && response.data) {
        // Store token in localStorage
        localStorage.setItem('cleanStudioToken', response.data.access_token);
        localStorage.setItem('cleanStudioUser', JSON.stringify({
          userId: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          isAuthenticated: true,
          authType: 'email',
        }));
        return response.data;
      }

      return rejectWithValue(response.message || 'Login failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

// Async thunk for email verification
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyEmail(token);

      if (response.success && response.data) {
        // If verification returns a token, store it for auto-login
        if (response.data.access_token) {
          localStorage.setItem('cleanStudioToken', response.data.access_token);
          localStorage.setItem('cleanStudioUser', JSON.stringify({
            userId: response.data.user_id,
            email: response.data.email,
            name: response.data.name,
            isAuthenticated: true,
            authType: 'email',
          }));
        }
        return response.data;
      }

      return rejectWithValue(response.message || 'Verification failed');
    } catch (error) {
      return rejectWithValue(error.message || 'Verification failed');
    }
  }
);

// Async thunk for resending verification email
export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authService.resendVerification(email);

      if (response.success) {
        return { message: response.message };
      }

      return rejectWithValue(response.message || 'Failed to resend verification email');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to resend verification email');
    }
  }
);

// Async thunk for changing password (authenticated users)
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) {
        return rejectWithValue('Not authenticated');
      }

      const response = await authService.changePassword(currentPassword, newPassword, token);

      if (response.success) {
        return { message: response.message };
      }

      return rejectWithValue(response.message || 'Failed to change password');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to change password');
    }
  }
);

// Async thunk for updating user profile
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async ({ name }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) {
        return rejectWithValue('Not authenticated');
      }

      const response = await authService.updateProfile({ name }, token);

      if (response.success) {
        // Update localStorage with new name
        const storedUser = JSON.parse(localStorage.getItem('cleanStudioUser') || '{}');
        if (response.data?.name) {
          storedUser.name = response.data.name;
          localStorage.setItem('cleanStudioUser', JSON.stringify(storedUser));
        }
        return response.data;
      }

      return rejectWithValue(response.message || 'Failed to update profile');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

// Async thunk for forgot password (request reset email)
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);

      if (response.success) {
        return { message: response.message };
      }

      return rejectWithValue(response.message || 'Failed to send reset email');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send reset email');
    }
  }
);

// Async thunk for resetting password with token
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(token, newPassword);

      if (response.success && response.data) {
        // Store token in localStorage for auto-login
        localStorage.setItem('cleanStudioToken', response.data.access_token);
        localStorage.setItem('cleanStudioUser', JSON.stringify({
          userId: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          isAuthenticated: true,
          authType: 'email',
        }));
        return response.data;
      }

      return rejectWithValue(response.message || 'Failed to reset password');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reset password');
    }
  }
);

// Helper to load user from localStorage
const loadUserFromStorage = () => {
  try {
    const userStr = localStorage.getItem('cleanStudioUser');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (e) {
    console.error('Error loading user from storage:', e);
  }
  return null;
};

const loadTokenFromStorage = () => {
  const token = localStorage.getItem('cleanStudioToken');
  if (token && token !== 'null' && authService.isTokenValid(token)) {
    return token;
  }
  return null;
};

const storedUser = loadUserFromStorage();
const storedToken = loadTokenFromStorage();

const initialState = {
  // User info
  user: storedUser,
  isAuthenticated: storedUser?.isAuthenticated || false,

  // Token
  token: storedToken,

  // Email verification pending (after signup, before verification)
  pendingVerification: null,

  // UI state
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Logout action
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.pendingVerification = null;
      state.status = 'idle';
      state.error = null;

      // Clear localStorage
      localStorage.removeItem('cleanStudioUser');
      localStorage.removeItem('cleanStudioToken');
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Restore session from localStorage (called on app init)
    restoreSession: (state) => {
      const user = loadUserFromStorage();
      const token = loadTokenFromStorage();

      if (user && token) {
        state.user = user;
        state.isAuthenticated = user.isAuthenticated || false;
        state.token = token;
      }
    },

    // Set token (for anonymous users migrating to new system)
    setToken: (state, action) => {
      state.token = action.payload;
      localStorage.setItem('cleanStudioToken', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Google Sign-In
      .addCase(googleSignIn.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(googleSignIn.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        state.user = {
          userId: action.payload.user_id,
          email: action.payload.email,
          name: action.payload.name,
          isAuthenticated: true,
          authType: 'google',
        };
        state.error = null;
      })
      .addCase(googleSignIn.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Sign-in failed';
      })

      // Anonymous token
      .addCase(createAnonymousToken.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createAnonymousToken.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.access_token;
        // Don't set isAuthenticated for anonymous users
      })
      .addCase(createAnonymousToken.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create token';
      })

      // Email/password signup
      .addCase(signup.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Don't set isAuthenticated until email is verified
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        // Store pending verification info for UI
        state.pendingVerification = {
          email: action.payload.email,
          name: action.payload.name,
        };
        state.error = null;
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = 'failed';
        // Handle both string and object error formats
        const payload = action.payload;
        state.error = typeof payload === 'object' ? payload.message : (payload || 'Signup failed');
      })

      // Email/password login
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        state.user = {
          userId: action.payload.user_id,
          email: action.payload.email,
          name: action.payload.name,
          isAuthenticated: true,
          authType: 'email',
        };
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Login failed';
      })

      // Email verification
      .addCase(verifyEmail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload.access_token) {
          state.isAuthenticated = true;
          state.token = action.payload.access_token;
          state.user = {
            userId: action.payload.user_id,
            email: action.payload.email,
            name: action.payload.name,
            isAuthenticated: true,
            authType: 'email',
          };
        }
        // Clear pending verification state
        state.pendingVerification = null;
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Verification failed';
      })

      // Resend verification
      .addCase(resendVerification.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resendVerification.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to resend verification';
      })

      // Change password
      .addCase(changePassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to change password';
      })

      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.user && action.payload?.name) {
          state.user.name = action.payload.name;
        }
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update profile';
      })

      // Forgot password
      .addCase(forgotPassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to send reset email';
      })

      // Reset password
      .addCase(resetPassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.isAuthenticated = true;
        state.token = action.payload.access_token;
        state.user = {
          userId: action.payload.user_id,
          email: action.payload.email,
          name: action.payload.name,
          isAuthenticated: true,
        };
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to reset password';
      });
  },
});

export const { logout, clearError, restoreSession, setToken } = authSlice.actions;

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthToken = (state) => state.auth.token;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;
export const selectPendingVerification = (state) => state.auth.pendingVerification;

export default authSlice.reducer;
